import {
  Bleed,
  Box,
  Checkbox,
  Divider,
  IconChevron,
  IconClear,
  Text,
} from 'braid-design-system';
import React, { createRef, useEffect, useState } from 'react';
// @ts-expect-error no type defs
import IsolatedScroll from 'react-isolated-scroll';

import { useClickOutside } from 'src/hooks/useClickOutside';
import type { Industry, IndustryGrouping } from 'src/types/industry';

import * as styles from './IndustryClassificationsField.css';

export interface SelectBoxIndustryGrouping extends IndustryGrouping {
  industries: Array<Industry & { selected: boolean }>;
  selected: boolean;
}

interface Props {
  placeholder: string;
  industryGroupings: SelectBoxIndustryGrouping[];
  hideClearAll?: boolean;
  setIndustryGroupings: (industryGroups: SelectBoxIndustryGrouping[]) => void;
}

/*
  Styling and some functionality for the text field portion of this component taken from Chalice.
  https://github.com/SEEK-Jobs/chalice/tree/master/src/components/Search/SearchBar/ClassificationsField
 */

const IndustryClassificationsField = ({
  placeholder,
  industryGroupings,
  hideClearAll,
  setIndustryGroupings,
}: Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [displayText, setDisplayText] = useState('');

  const toggleAllIndustries = (
    targetGrouping: IndustryGrouping,
    groupingSelected: boolean,
    industriesSelected: boolean,
  ) => {
    const newGroupings = industryGroupings.map((grouping) => {
      if (grouping.title === targetGrouping.title) {
        return {
          ...grouping,
          selected: groupingSelected,
          industries: grouping.industries.map((industry) => ({
            ...industry,
            selected: industriesSelected,
          })),
        };
      }
      return grouping;
    });
    return newGroupings;
  };

  const toggleSingleIndustry = (
    targetGrouping: IndustryGrouping,
    groupingSelected: boolean,
    targetIndustry: Industry,
    industrySelected: boolean,
  ) => {
    const newGroupings = industryGroupings.map((grouping) => {
      if (grouping.title === targetGrouping.title) {
        return {
          ...grouping,
          selected: groupingSelected,
          industries: grouping.industries.map((industry) => {
            if (industry.id === targetIndustry.id) {
              return {
                ...industry,
                selected: industrySelected,
              };
            }
            return industry;
          }),
        };
      }
      return grouping;
    });
    return newGroupings;
  };

  useEffect(() => {
    const selectedTitles = industryGroupings.flatMap(({ selected, title }) =>
      selected ? [title] : [],
    );
    if (selectedTitles.length === 0) {
      setDisplayText('');
    } else if (selectedTitles.length === 1) {
      setDisplayText(selectedTitles[0]);
    } else {
      setDisplayText(`${selectedTitles.length} industries`);
    }
  }, [industryGroupings]);

  const menuRef = createRef<HTMLDivElement>();
  useClickOutside(menuRef, () => {
    setMenuOpen(false);
  });

  return (
    <Box ref={menuRef}>
      <Box
        component="label"
        data-testid="industryField"
        background="surface"
        className={styles.field}
        onClick={() => {
          setMenuOpen(!menuOpen);
        }}
      >
        <Text
          baseline={false}
          tone={displayText.length === 0 ? 'secondary' : undefined}
        >
          <Box
            data-testid="industryDisplayText"
            component="span"
            className={[
              styles.text,
              false ? styles.shortenForClearLink : undefined,
            ]}
          >
            {displayText.length === 0 ? placeholder : displayText}
          </Box>
        </Text>
        <Box
          display="flex"
          alignItems="center"
          transition="fast"
          position="absolute"
          right={0}
          top={0}
          className={
            menuOpen && !hideClearAll ? undefined : styles.hideClearLink
          }
        >
          <Box
            component="span"
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="touchable"
            width="touchable"
            onClick={() => {}}
          >
            <Text baseline={false}>
              <IconChevron
                direction={menuOpen ? 'up' : 'down'}
                alignY="lowercase"
              />
            </Text>
          </Box>

          <Box
            data-testid="clearButton"
            className={styles.clearLink}
            onClick={() => {
              setIndustryGroupings(
                industryGroupings.map((g) => ({
                  ...{
                    ...g,
                    industries: g.industries.map((industry) => ({
                      ...industry,
                      selected: false,
                    })),
                  },
                  selected: false,
                })),
              );
              setMenuOpen(false);
            }}
          >
            <Text tone="secondary">
              <IconClear />
            </Text>
          </Box>
        </Box>
      </Box>

      {menuOpen && (
        <Bleed bottom="large" right="large">
          <Box paddingTop="small">
            <IsolatedScroll
              className={styles.menuBox}
              data-testid="industryDropdownMenu"
            >
              <Box background="surface" borderRadius="standard">
                {industryGroupings.map((grouping, groupingIndex) => (
                  <Box key={groupingIndex}>
                    <Box className={styles.checkBox} padding="xsmall">
                      <Checkbox
                        id={grouping.title}
                        checked={grouping.selected}
                        onChange={() => {
                          if (grouping.selected) {
                            setIndustryGroupings(
                              toggleAllIndustries(grouping, false, false),
                            );
                          } else {
                            setIndustryGroupings(
                              industryGroupings.map((g) =>
                                g.title === grouping.title
                                  ? { ...g, selected: !g.selected }
                                  : g,
                              ),
                            );
                          }
                        }}
                        label={grouping.title}
                      />
                    </Box>

                    {grouping.selected && (
                      <Box paddingLeft="medium">
                        <Box className={styles.checkBox} padding="xsmall">
                          <Checkbox
                            checked={grouping.industries.every(
                              (i) => !i.selected,
                            )}
                            id={`${grouping.title}-all`}
                            onChange={() => {
                              if (
                                grouping.industries.every((i) => !i.selected)
                              ) {
                                setIndustryGroupings(
                                  toggleAllIndustries(grouping, false, false),
                                );
                              } else {
                                setIndustryGroupings(
                                  toggleAllIndustries(
                                    grouping,
                                    grouping.selected,
                                    false,
                                  ),
                                );
                              }
                            }}
                            label={`All ${grouping.title}`}
                          />
                        </Box>
                        <Divider />
                        {grouping.industries.map((industry, industryIndex) => (
                          <Box
                            className={styles.checkBox}
                            key={industryIndex}
                            padding="xsmall"
                          >
                            <Checkbox
                              checked={industry.selected}
                              id={industry.id}
                              onChange={() => {
                                setIndustryGroupings(
                                  toggleSingleIndustry(
                                    grouping,
                                    grouping.selected,
                                    industry,
                                    !industry.selected,
                                  ),
                                );
                              }}
                              label={industry.title}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </IsolatedScroll>
          </Box>
        </Bleed>
      )}
    </Box>
  );
};

export default IndustryClassificationsField;
